import React, {useRef, useState} from "react";
import {
    Box,
    Button,
    Checkbox,
    ClickAwayListener, Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Popover,
    TextField
} from "@mui/material";
import {
    performancePopoverPaperSx,
    performanceSecondaryControlSx,
} from '../styles/performanceMsiStyles';
import {
    APP_STYLE_COMPONENT_COLORS as MSI_COMPONENT_COLORS,
    APP_STYLE_PALETTE as MSI_PALETTE,
    usesAppStyleGrammar,
} from '../styles/msiStyleTokens';

export default function FactorSelectDropdown({
    factors,                 // string[]
    selected,                // string[]
    onChange,                // (nextSelected: string[]) => void
    getColor,                // (factor: string) => string
    buttonLabel = 'Factors',
    maxHeight = 320,
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const anchorRef = useRef(null);

    const toggle = () => setOpen((p) => !p);
    const closeIfClickAway = (e) => {
        if (anchorRef.current?.contains(e.target)) return;
        setOpen(false);
    };

    const handleToggleFactor = (f) => {
        const set = new Set(selected);
        if (set.has(f)) set.delete(f);
        else set.add(f);
        onChange(Array.from(set));
    };

    return (
        <>
            <Button
                ref={anchorRef}
                variant="outlined"
                size="small"
                onClick={toggle}
                sx={{
                    height: 32,
                    ...performanceSecondaryControlSx,
                }}
            >
                {buttonLabel} ({selected.length})
            </Button>

            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { width: 360, ...performancePopoverPaperSx } }}
            >
                <ClickAwayListener onClickAway={closeIfClickAway}>
                    <Box>
                        <TextField size="small" fullWidth placeholder="Search factors" aria-label="Search factors" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ p: 1 }} />
                        <Box
                            sx={{
                                maxHeight,
                                overflowY: 'auto',
                                p: 1,
                            }}
                        >
                            <List dense disablePadding>
                                {factors.filter((factor) => factor.toLowerCase().includes(search.toLowerCase())).map((f) => {
                                    const checked = selected.includes(f);
                                    return (
                                        <ListItem
                                            key={f}
                                            sx={{
                                                py: 0.5,
                                                px: 1,
                                                borderRadius: 1,
                                                '&:hover': {
                                                    bgcolor: (theme) => (usesAppStyleGrammar(theme)
                                                        ? MSI_COMPONENT_COLORS.rowHover
                                                        : '#131a21'),
                                                },
                                            }}
                                            onClick={() => handleToggleFactor(f)}
                                            secondaryAction={
                                                <Checkbox
                                                    edge="end"
                                                    checked={checked}
                                                    onChange={() => handleToggleFactor(f)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    size="small"
                                                />
                                            }
                                        >
                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        bgcolor: getColor(f),
                                                    }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primaryTypographyProps={{
                                                    sx: {
                                                        color: (theme) => (usesAppStyleGrammar(theme)
                                                            ? MSI_PALETTE.ink700
                                                            : '#ddd'),
                                                        fontSize: 13,
                                                    },
                                                }}
                                                primary={f}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                        <Divider sx={{ borderColor: (theme) => (usesAppStyleGrammar(theme) ? MSI_PALETTE.line : '#333') }} />
                        <Box sx={{ p: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => onChange([])}
                                sx={performanceSecondaryControlSx}
                            >
                                Clear
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => setOpen(false)}
                                sx={{
                                    bgcolor: (theme) => (usesAppStyleGrammar(theme)
                                        ? MSI_PALETTE.blue600
                                        : '#11161c'),
                                }}
                            >
                                OK
                            </Button>
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Popover>
        </>
    );
}
